import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NAV_SECTIONS } from '@/lib/constants';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { hasPermission } from '@/lib/permissions';
import { isPartner, partnerMayOpen } from '@/lib/partner';
import {
  LayoutDashboard,
  QrCode,
  Plus,
  Palette,
  Pencil,
  BarChart3,
  Shield,
  Settings,
  Link as LinkIcon,
  FileText,
  Inbox,
  Link2,
  Target,
  Users,
  Image,
  Workflow,
  Webhook,
  FileBarChart,
  FlaskConical,
  TrendingUp,
  Key,
  LayoutTemplate,
  AlertCircle,
  Wand2,
  Layers,
  Sparkles,
  User,
  ImagePlus,
  BookOpen,
} from 'lucide-react';

// Icon mapping
const iconMap = {
  LayoutDashboard,
  QrCode,
  Plus,
  Palette,
  Pencil,
  BarChart3,
  Shield,
  Settings,
  Link: LinkIcon,
  FileText,
  Inbox,
  Link2,
  Target,
  Users,
  Image,
  Workflow,
  Webhook,
  FileBarChart,
  FlaskConical,
  TrendingUp,
  Key,
  LayoutTemplate,
  AlertCircle,
  Wand2,
  Layers,
  Sparkles,
  User,
  ImagePlus,
  BookOpen,
};

export function Sidebar() {
  const location = useLocation();
  const { appTheme } = useAppTheme();
  const { theme: colorMode } = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Determine which logo to use based on color mode
  const logoUrl = colorMode === 'dark' && appTheme.admin_logo_dark 
    ? appTheme.admin_logo_dark 
    : appTheme.admin_logo;

  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname.startsWith(path);
  };

  return (
    <ScrollArea className="h-[calc(100vh-4rem)]">
      <div className="flex flex-col gap-4 p-4">
        {/* Logo for mobile sheet */}
        <div className="flex items-center gap-2 px-2 lg:hidden">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="LinkHub" 
              className="h-8 w-auto object-contain"
            />
          ) : (
            <>
              <div 
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: appTheme.primary_color || '#154734' }}
              >
                <QrCode className="h-5 w-5 text-white" />
              </div>
              <span className="font-semibold text-lg">LinkHub</span>
            </>
          )}
        </div>
        <Separator className="lg:hidden" />

        {/* Navigation sections */}
        {NAV_SECTIONS.map((section, sectionIndex) => {
          // Hide admin-only items (e.g. Collateral) from non-admins, and
          // permission items (e.g. Audience Extracts) from users who don't
          // hold the permission; drop a section if nothing remains visible.
          const items = section.items.filter(
            (item) =>
              (!item.adminOnly || isAdmin) &&
              (!item.permission || hasPermission(user, item.permission)) &&
              (!isPartner(user) || partnerMayOpen(item.path)),
          );
          if (items.length === 0) return null;
          return (
          <div key={section.title} className="space-y-2">
            <h3 className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {section.title}
            </h3>
            <nav className="space-y-1">
              {items.map((item) => {
                const Icon = iconMap[item.icon];
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                      active
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                    aria-current={active ? 'page' : undefined}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            {sectionIndex < NAV_SECTIONS.length - 1 && (
              <Separator className="my-2" />
            )}
          </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

export default Sidebar;
